import React, { useCallback, useEffect, useState } from 'react';
import { DatePicker, Select, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import ParcelFilterStyle from '../../../../delivery/components/ParcelFilterStyle';
import { getSmartLabelClassName } from '../../../../../utils/labelWidthUtils';
import { useFilterLabelClassName, useShouldUseVerticalLayout } from '../../../../../utils/languageUtils';
import type { WorkProgressBagCustomerOption } from '../../graphql/queries/getWorkProgressBagCustomerSummaryPage';

const { RangePicker } = DatePicker;

/** 大包进度：仅本 Tab；与「全量进度」RangePicker 限制无关 */
const BIG_BAG_LOOKBACK_DAYS = 90;
const BIG_BAG_MAX_SPAN_DAYS = 90;

export interface BigBagProgressFiltersValue {
  timeRange: { start: Dayjs; end: Dayjs };
  customerId: number | null;
}

interface BigBagProgressFiltersProps {
  onChange: (value: BigBagProgressFiltersValue) => void;
  /** 与汇总表同条件下的客户去重列表（与 allMawbs 同源、一次请求） */
  customerOptions?: WorkProgressBagCustomerOption[];
}

const BigBagProgressFilters: React.FC<BigBagProgressFiltersProps> = ({
  onChange,
  customerOptions = [],
}) => {
  const { t } = useTranslation();
  const useVerticalLayout = useShouldUseVerticalLayout();

  type Preset = 'today' | 'last3days' | 'last7days' | 'custom';

  const getRangeByPreset = (preset: Preset): [Dayjs, Dayjs] => {
    const end = dayjs().startOf('day');
    switch (preset) {
      case 'today':
        return [end, end];
      case 'last3days':
        return [end.subtract(2, 'day'), end];
      case 'last7days':
      default:
        return [end.subtract(6, 'day'), end];
    }
  };

  const getDefaultTimeRange = (): [Dayjs, Dayjs] => getRangeByPreset('last7days');

  const [timeRangePreset, setTimeRangePreset] = useState<Preset>('last7days');
  const [timeRange, setTimeRange] = useState<[string | null, string | null] | null>(() => {
    const [start, end] = getDefaultTimeRange();
    return [start.toISOString(), end.toISOString()];
  });
  const [customerId, setCustomerId] = useState<number | null>(null);

  // 初始化默认值并上报
  useEffect(() => {
    const [start, end] = getDefaultTimeRange();
    onChange({
      timeRange: { start, end },
      customerId: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = (next?: Partial<BigBagProgressFiltersValue>) => {
    const [startIso, endIso] = timeRange || [null, null];
    const start = startIso ? dayjs(startIso).startOf('day') : getDefaultTimeRange()[0];
    const end = endIso ? dayjs(endIso).startOf('day') : getDefaultTimeRange()[1];

    onChange({
      timeRange: next?.timeRange ?? { start, end },
      customerId: next && 'customerId' in next ? next.customerId! : customerId,
    });
  };

  const handleTimeRangePresetChange = (preset: Preset) => {
    setTimeRangePreset(preset);
    if (preset === 'custom') return;
    const [start, end] = getRangeByPreset(preset);
    setTimeRange([start.toISOString(), end.toISOString()]);
    emit({ timeRange: { start, end } });
  };

  const handleTimeRangeChange = (dates: [string | null, string | null] | null) => {
    setTimeRange(dates);
    if (dates && dates[0] && dates[1]) {
      setTimeRangePreset('custom');
      const start = dayjs(dates[0]).minute(0).second(0).millisecond(0);
      const end = dayjs(dates[1]).minute(0).second(0).millisecond(0);
      emit({ timeRange: { start, end } });
    }
  };

  const disabledDateForBigBag = useCallback((current: Dayjs | null) => {
    if (!current) return false;
    const minDay = dayjs().startOf('day').subtract(BIG_BAG_LOOKBACK_DAYS, 'day');
    const maxDay = dayjs().endOf('day');
    return current < minDay || current > maxDay;
  }, []);

  const handleTimeRangePickerChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      const diffInDays = dates[1].startOf('day').diff(dates[0].startOf('day'), 'day');
      if (diffInDays > BIG_BAG_MAX_SPAN_DAYS) {
        message.warning(
          t('wms.progress.bigBag.messages.maxRange', { days: BIG_BAG_MAX_SPAN_DAYS })
        );
        return;
      }
      handleTimeRangeChange([dates[0].toISOString(), dates[1].toISOString()]);
      return;
    }
    handleTimeRangeChange(null);
  };

  const handleCustomerChange = (value: string | undefined) => {
    const next = value ? Number(value) : null;
    setCustomerId(next);
    emit({ customerId: next });
  };

  const [startIso, endIso] = timeRange || [null, null];
  const selectedStart = startIso ? dayjs(startIso).startOf('day') : null;
  const selectedEnd = endIso ? dayjs(endIso).startOf('day') : null;

  const colClientSpanClassName = getSmartLabelClassName([t('wms.progress.filters.clientName')]);
  const timeRangeLabelClassName = useFilterLabelClassName(colClientSpanClassName);
  const clientNameLabelClassName = useFilterLabelClassName(colClientSpanClassName);

  const customerSelectOptions = customerOptions.map(c => ({
    value: String(c.customerId),
    label: c.customerName,
  }));

  return (
    <div className="mb-2 pl-0 py-2">
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
        <div className="w-full lg:basis-2/3 lg:max-w-[66%] min-w-0">
          {useVerticalLayout ? (
            <div className="grid gap-x-10 gap-y-1.5" style={{ gridTemplateColumns: '1.85fr 1fr' }}>
              {/* 第一行：两个标题 */}
              <div className="min-w-0">
                <span className={timeRangeLabelClassName}>{t('wms.progress.filters.timeRange')}</span>
              </div>
              <div className="min-w-0">
                <span className={clientNameLabelClassName}>{t('wms.progress.filters.clientName')}</span>
              </div>
              {/* 第二行：时间预设+范围、客户名称下拉 */}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Select
                    value={timeRangePreset}
                    onChange={handleTimeRangePresetChange}
                    className="w-[205px]"
                    size="middle"
                    options={[
                      { value: 'today', label: t('wms.progress.bigBag.timePresets.today') },
                      { value: 'last3days', label: t('wms.progress.bigBag.timePresets.last3days') },
                      { value: 'last7days', label: t('wms.progress.bigBag.timePresets.last7days') },
                      { value: 'custom', label: t('wms.progress.bigBag.timePresets.custom') },
                    ]}
                  />
                  <RangePicker
                    value={selectedStart && selectedEnd ? [selectedStart, selectedEnd] : null}
                    onChange={handleTimeRangePickerChange}
                    disabled={timeRangePreset !== 'custom'}
                    className="flex-1 w-0 min-w-0 max-w-full h-8"
                    format="YYYY/MM/DD"
                    size="middle"
                    allowClear
                    disabledDate={disabledDateForBigBag}
                  />
                </div>
              </div>
              <div className="min-w-0">
                <Select
                  options={customerSelectOptions}
                  value={customerId != null ? String(customerId) : undefined}
                  onChange={(value: string | undefined) => handleCustomerChange(value)}
                  className="w-full"
                  size="middle"
                  placeholder={t('components.input.all')}
                  allowClear
                  showSearch
                />
              </div>
            </div>
          ) : (
            <div className="grid gap-x-10 gap-y-4" style={{ gridTemplateColumns: '1.5fr 1fr' }}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Select
                    value={timeRangePreset}
                    onChange={handleTimeRangePresetChange}
                    className="w-[150px]"
                    size="middle"
                    options={[
                      { value: 'today', label: t('wms.progress.bigBag.timePresets.today') },
                      { value: 'last3days', label: t('wms.progress.bigBag.timePresets.last3days') },
                      { value: 'last7days', label: t('wms.progress.bigBag.timePresets.last7days') },
                      { value: 'custom', label: t('wms.progress.bigBag.timePresets.custom') },
                    ]}
                  />
                  <RangePicker
                    value={selectedStart && selectedEnd ? [selectedStart, selectedEnd] : null}
                    onChange={handleTimeRangePickerChange}
                    disabled={timeRangePreset !== 'custom'}
                    className="flex-1 min-w-[230px] max-w-full h-8"
                    format="YYYY/MM/DD"
                    size="middle"
                    allowClear
                    disabledDate={disabledDateForBigBag}
                  />
                </div>
              </div>
              <div className="min-w-0">
                <ParcelFilterStyle
                  label={t('wms.progress.filters.clientName')}
                  options={customerSelectOptions}
                  value={customerId != null ? String(customerId) : undefined}
                  onChange={(value: string | undefined) => handleCustomerChange(value)}
                  className="w-full"
                  spanClassName={colClientSpanClassName}
                />
              </div>
            </div>
          )}
        </div>
        {/* showBagExpectedDiff 开关已被「包裹差异 / 大包差异」子 tab 取代 */}
      </div>
    </div>
  );
};

export default BigBagProgressFilters;

